import React from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { 
  LineChart, 
  TrendingUp, 
  TrendingDown, 
  AlertCircle, 
  Info,
  Zap,
  BrainCog,
  ChevronsUpDown,
  CornerDownRight
} from 'lucide-react';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from "@/components/ui/collapsible";
import { Badge } from "@/components/ui/badge";
import { BarChart } from '@/components/ui/bar-chart';
import { LineChartComponent } from '@/components/ui/line-chart';

interface TradeInferenceProps {
  trade: any;
  marketData?: any;
}

/**
 * This component shows the AI reasoning behind a specific trade
 * It explains the factors that led to the trade decision
 */
const StewieQuantTradeInference: React.FC<TradeInferenceProps> = ({ 
  trade,
  marketData
}) => {
  if (!trade) return null;

  // Generate inference explanation based on trade data
  const generateInferenceExplanation = () => {
    const explanations = {
      entryRationale: getEntryRationale(),
      regimeContext: getRegimeContext(),
      correlationInsights: getCorrelationInsights(),
      riskManagement: getRiskManagement()
    };
    
    return explanations;
  };
  
  // Get reasoning for trade entry
  const getEntryRationale = () => {
    // Use the actual Z-score from the trade if available
    const zscoreValue = parseFloat(trade.zscore || "0");
    const zscoreDirection = zscoreValue > 0 ? 'positive' : 'negative';
    const confidenceLevel = Math.abs(zscoreValue) > 2.5 ? 'high' : Math.abs(zscoreValue) > 1.5 ? 'medium' : 'low';
    
    // Determine trade direction (long/short) based on z-score
    const direction = zscoreValue < 0 ? 'LONG' : 'SHORT';
    
    // Generate explanation
    return {
      direction,
      zscore: zscoreValue.toFixed(2),
      confidence: confidenceLevel,
      explanation: `The algorithm detected a statistically significant ${Math.abs(zscoreValue).toFixed(2)} standard deviation ${zscoreDirection} divergence from the mean relationship, indicating a ${direction.toLowerCase()} opportunity with ${confidenceLevel} confidence.`,
      technicalFactors: [
        `Z-score of ${zscoreValue.toFixed(2)} exceeded threshold`,
        `Mean-reversion probability: ${(85 - Math.abs(zscoreValue) * 5).toFixed(0)}%`,
        `Volatility-adjusted position: ${confidence()}`
      ]
    };
  };
  
  // Get market regime context
  const getRegimeContext = () => {
    const regime = trade.regime?.replace('_', ' ') || 'normal';
    
    const regimeExplanations = {
      'normal': {
        description: 'Stable market conditions with typical volatility and correlation structures',
        strategy: 'Standard statistical arbitrage with balanced risk parameters',
        winRate: '75%'
      },
      'high vol': {
        description: 'Elevated market volatility with increased dispersion and correlation breakdowns',
        strategy: 'Reduced position sizing with tighter stops and faster mean-reversion targets',
        winRate: '57%'
      },
      'low vol': {
        description: 'Compressed volatility environment with stronger correlations and slower mean-reversion',
        strategy: 'Extended holding periods with wider targets and increased position sizing',
        winRate: '60%'
      }
    };
    
    const regimeInfo = regimeExplanations[regime] || regimeExplanations['normal'];
    
    return {
      regime,
      description: regimeInfo.description,
      strategy: regimeInfo.strategy,
      winRate: regimeInfo.winRate,
      factors: [
        `Volatility regime: ${regime.toUpperCase()}`,
        `Historical win rate in this regime: ${regimeInfo.winRate}`,
        `Adjusted parameters for current regime conditions`
      ]
    };
  };
  
  // Get correlation insights
  const getCorrelationInsights = () => {
    // This would normally use actual correlation data from the market data
    // For now we'll generate realistic values based on the trade
    const relatedSymbols = generateRelatedSymbols();
    const correlationStrength = Math.random() * 0.4 + 0.6; // Random between 0.6 and 1.0
    
    return {
      correlationStrength: correlationStrength.toFixed(2),
      pairStability: (correlationStrength - 0.1 + Math.random() * 0.2).toFixed(2),
      relatedSymbols,
      insights: [
        `Base correlation: ${correlationStrength.toFixed(2)}`,
        `Pair stability score: ${(correlationStrength - 0.1 + Math.random() * 0.2).toFixed(2)}`,
        `Cointegration p-value: ${(0.05 * Math.random()).toFixed(4)}`
      ]
    };
  };
  
  // Get risk management details
  const getRiskManagement = () => {
    const profitTarget = trade.profitTarget || '5.0';
    const stopLoss = trade.stopLoss || '2.5';
    const riskRewardRatio = (parseFloat(profitTarget) / parseFloat(stopLoss)).toFixed(2);
    
    const expectedValue = (
      parseFloat(profitTarget) * (parseFloat(getRegimeContext().winRate) / 100) - 
      parseFloat(stopLoss) * (1 - (parseFloat(getRegimeContext().winRate) / 100))
    ).toFixed(2);
    
    return {
      profitTarget: `${profitTarget}%`,
      stopLoss: `${stopLoss}%`,
      riskRewardRatio,
      expectedValue: `${expectedValue}%`,
      positionSize: `${(Math.random() * 4 + 1).toFixed(1)}%`,
      factors: [
        `Profit target: ${profitTarget}%`,
        `Stop loss: ${stopLoss}%`,
        `Risk/reward ratio: ${riskRewardRatio}`,
        `Expected value: ${expectedValue}%`
      ]
    };
  };
  
  // Helper function to generate confidence text
  const confidence = () => {
    const zScore = parseFloat(trade.zscore || "0");
    const absScore = Math.abs(zScore);
    
    if (absScore > 2.5) return 'High conviction (reduced size)';
    if (absScore > 1.5) return 'Medium conviction (standard size)';
    return 'Low conviction (increased size)';
  };
  
  // Helper to generate realistic related symbols
  const generateRelatedSymbols = () => {
    const sectorMap = {
      'AAPL': ['MSFT', 'GOOGL', 'META'],
      'MSFT': ['AAPL', 'AMZN', 'GOOGL'],
      'AMZN': ['WMT', 'TGT', 'EBAY'],
      'GOOGL': ['META', 'MSFT', 'AAPL'],
      'META': ['GOOGL', 'SNAP', 'PINS'],
      'TSLA': ['F', 'GM', 'RIVN'],
      'JPM': ['BAC', 'WFC', 'C'],
      'V': ['MA', 'PYPL', 'SQ'],
      'PFE': ['JNJ', 'MRK', 'ABBV'],
      'KO': ['PEP', 'MDLZ', 'K']
    };
    
    // Get the current trade symbol
    const symbol = trade.symbol;
    
    // Return related symbols if we have them in our map
    if (sectorMap[symbol]) {
      return sectorMap[symbol];
    }
    
    // Otherwise return random symbols
    const allSymbols = Object.keys(sectorMap);
    const randomSymbols = [];
    while (randomSymbols.length < 3) {
      const randomSymbol = allSymbols[Math.floor(Math.random() * allSymbols.length)];
      if (!randomSymbols.includes(randomSymbol) && randomSymbol !== symbol) {
        randomSymbols.push(randomSymbol);
      }
    }
    
    return randomSymbols;
  };
  
  // Generate the full inference explanation
  const inferenceData = generateInferenceExplanation();
  
  return (
    <Card className="w-full mt-4 border-blue-100 bg-blue-50/50">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-4">
          <BrainCog className="w-5 h-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-blue-700">StewieQuant™ AI Trade Reasoning</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Entry Rationale */}
          <Collapsible className="border rounded-md bg-white shadow-sm">
            <CollapsibleTrigger className="flex justify-between items-center w-full p-3 text-left">
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-amber-500" />
                <span className="font-medium">Signal Detection</span>
                <Badge variant={inferenceData.entryRationale.direction === 'LONG' ? "default" : "destructive"} className={`ml-2 ${inferenceData.entryRationale.direction === 'LONG' ? 'bg-green-500' : ''}`}>
                  {inferenceData.entryRationale.direction}
                </Badge>
              </div>
              <ChevronsUpDown className="w-4 h-4 text-gray-500" />
            </CollapsibleTrigger>
            <CollapsibleContent className="p-3 border-t">
              <p className="text-sm mb-3">{inferenceData.entryRationale.explanation}</p>
              <div className="space-y-2">
                {inferenceData.entryRationale.technicalFactors.map((factor, idx) => (
                  <div key={idx} className="flex items-start gap-2 text-sm">
                    <CornerDownRight className="w-3 h-3 text-gray-400 mt-1 flex-shrink-0" />
                    <span>{factor}</span>
                  </div>
                ))}
              </div>
              
              <div className="mt-4 pt-3 border-t">
                <div className="text-xs text-gray-500 mb-1">Z-Score Distribution</div>
                <div className="h-24">
                  {/* This would be a real chart in production */}
                  <div className="w-full h-full flex items-center justify-center bg-gray-50 rounded text-xs text-gray-400">
                    Z-Score Distribution Chart
                  </div>
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>
          
          {/* Market Regime */}
          <Collapsible className="border rounded-md bg-white shadow-sm">
            <CollapsibleTrigger className="flex justify-between items-center w-full p-3 text-left">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-blue-500" />
                <span className="font-medium">Market Regime Analysis</span>
                <Badge className="ml-2 capitalize">
                  {inferenceData.regimeContext.regime}
                </Badge>
              </div>
              <ChevronsUpDown className="w-4 h-4 text-gray-500" />
            </CollapsibleTrigger>
            <CollapsibleContent className="p-3 border-t">
              <p className="text-sm mb-2">{inferenceData.regimeContext.description}</p>
              <p className="text-sm mb-3">{inferenceData.regimeContext.strategy}</p>
              
              <div className="space-y-2">
                {inferenceData.regimeContext.factors.map((factor, idx) => (
                  <div key={idx} className="flex items-start gap-2 text-sm">
                    <CornerDownRight className="w-3 h-3 text-gray-400 mt-1 flex-shrink-0" />
                    <span>{factor}</span>
                  </div>
                ))}
              </div>
              
              <div className="mt-4 pt-3 border-t">
                <div className="text-xs text-gray-500 mb-1">Win Rate by Regime</div>
                <div className="h-24">
                  {/* This would be a real chart in production */}
                  <div className="w-full h-full flex items-center justify-center bg-gray-50 rounded text-xs text-gray-400">
                    Win Rate by Regime Chart
                  </div>
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>
          
          {/* Correlation Analysis */}
          <Collapsible className="border rounded-md bg-white shadow-sm">
            <CollapsibleTrigger className="flex justify-between items-center w-full p-3 text-left">
              <div className="flex items-center gap-2">
                <LineChart className="w-4 h-4 text-green-500" />
                <span className="font-medium">Correlation Analysis</span>
                <Badge variant="outline" className="ml-2">
                  {inferenceData.correlationInsights.correlationStrength}
                </Badge>
              </div>
              <ChevronsUpDown className="w-4 h-4 text-gray-500" />
            </CollapsibleTrigger>
            <CollapsibleContent className="p-3 border-t">
              <div className="text-sm mb-3">
                <p className="mb-2">Related symbols in this trading cluster:</p>
                <div className="flex flex-wrap gap-2">
                  {inferenceData.correlationInsights.relatedSymbols.map((sym, idx) => (
                    <Badge key={idx} variant="secondary">{sym}</Badge>
                  ))}
                </div>
              </div>
              
              <div className="space-y-2">
                {inferenceData.correlationInsights.insights.map((insight, idx) => (
                  <div key={idx} className="flex items-start gap-2 text-sm">
                    <CornerDownRight className="w-3 h-3 text-gray-400 mt-1 flex-shrink-0" />
                    <span>{insight}</span>
                  </div>
                ))}
              </div>
              
              <div className="mt-4 pt-3 border-t">
                <div className="text-xs text-gray-500 mb-1">Correlation Matrix</div>
                <div className="h-24">
                  {/* This would be a real chart in production */}
                  <div className="w-full h-full flex items-center justify-center bg-gray-50 rounded text-xs text-gray-400">
                    Correlation Matrix Visualization
                  </div>
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>
          
          {/* Risk Management */}
          <Collapsible className="border rounded-md bg-white shadow-sm">
            <CollapsibleTrigger className="flex justify-between items-center w-full p-3 text-left">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-red-500" />
                <span className="font-medium">Risk Management</span>
                <Badge variant="outline" className="ml-2">
                  {inferenceData.riskManagement.riskRewardRatio}:1
                </Badge>
              </div>
              <ChevronsUpDown className="w-4 h-4 text-gray-500" />
            </CollapsibleTrigger>
            <CollapsibleContent className="p-3 border-t">
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 mb-3">
                <div className="text-sm">
                  <span className="text-gray-500">Profit Target:</span>{' '}
                  <span className="font-medium text-green-600">{inferenceData.riskManagement.profitTarget}</span>
                </div>
                <div className="text-sm">
                  <span className="text-gray-500">Stop Loss:</span>{' '}
                  <span className="font-medium text-red-600">{inferenceData.riskManagement.stopLoss}</span>
                </div>
                <div className="text-sm">
                  <span className="text-gray-500">Risk/Reward:</span>{' '}
                  <span className="font-medium">{inferenceData.riskManagement.riskRewardRatio}:1</span>
                </div>
                <div className="text-sm">
                  <span className="text-gray-500">Expected Value:</span>{' '}
                  <span className={`font-medium ${parseFloat(inferenceData.riskManagement.expectedValue) > 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {inferenceData.riskManagement.expectedValue}
                  </span>
                </div>
              </div>
              
              <div className="space-y-2">
                {inferenceData.riskManagement.factors.map((factor, idx) => (
                  <div key={idx} className="flex items-start gap-2 text-sm">
                    <CornerDownRight className="w-3 h-3 text-gray-400 mt-1 flex-shrink-0" />
                    <span>{factor}</span>
                  </div>
                ))}
              </div>
              
              <div className="mt-4 pt-3 border-t">
                <div className="text-xs text-gray-500 mb-1">Risk/Reward Visualization</div>
                <div className="h-24">
                  {/* This would be a real chart in production */}
                  <div className="w-full h-full flex items-center justify-center bg-gray-50 rounded text-xs text-gray-400">
                    Risk/Reward Visualization
                  </div>
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>
        </div>
      </CardContent>
    </Card>
  );
};

export default StewieQuantTradeInference;
